/* Transferhistorie eines Spielers — reine Funktionen, keine Texte, kein DOM.
 *
 * Eine Zeile in der Tabelle player_transfers (supabase/migrations/
 * 20261004000000_player_transfers.sql) ist ein Wechsel: Datum, Art, abgebender
 * und aufnehmender Verein samt Logo, Notiz. Es gibt bewusst KEINE Gebühr und
 * keinen Weiterverkaufsanteil — die bleiben in player_contracts (admin, board,
 * data). Nichts in dieser Datei darf einen Betrag kennen oder anzeigen.
 *
 * Die Spalten from_afh und to_afh der Tabelle werden von der App nicht mehr
 * gelesen und nicht mehr geschrieben (Entscheidung Bastian, 2026-10-04): Beim
 * Anlegen gilt der Standard false, beim Ändern bleibt der Wert, wie er ist.
 *
 * Klubregister: Eine Zeile verweist mit from_club_id und to_club_id auf football_clubs. Die Spalten gibt es
 * erst nach supabase/klubregister-4-spalten.sql; ohne sie sind die Ids null. Name und Logo-Pfad stehen weiter
 * als Text in der Zeile (Übergangsregel, domain/footballClub.js stampClub). Die Verweis-Spalten stehen
 * bewusst NICHT in transferToRow, sondern in transferClubIdsToRow: supabase/transfers.js schreibt sie nur,
 * wenn die Datenbank sie kennt (supabase/clubColumns.js).
 *
 * Die Art einer Zeile liefert transferLabel() als Übersetzungsschlüssel; die
 * Komponente übersetzt.
 */

import { CLUBS } from './clubs.js';
import { clubKey, clubsById, resolveClub } from './footballClub.js';

export const TRANSFER_KINDS = ['permanent', 'loan', 'loan_end'];

/** Längste erlaubte Notiz — entspricht dem Check player_transfers_note_len. */
export const NOTE_MAX = 140;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function str(v) {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}
function orNull(v) {
  const s = str(v).trim();
  return s === '' ? null : s;
}

/** Zeile der Datenbank (snake_case) -> Objekt der App (camelCase). Fehlende Werte bekommen Vorgaben. */
export function rowToTransfer(row) {
  const r = row || {};
  return {
    id: r.id,
    playerId: r.player_id,
    date: str(r.transfer_date),
    kind: str(r.kind),
    fromClub: str(r.from_club),
    fromClubLogoPath: r.from_club_logo_path || null,
    fromClubId: r.from_club_id || null,
    toClub: str(r.to_club),
    toClubLogoPath: r.to_club_logo_path || null,
    toClubId: r.to_club_id || null,
    note: str(r.note),
    createdAt: str(r.created_at),
  };
}

/**
 * Objekt der App -> Zeile zum Schreiben. Ohne id, created_at und created_by:
 * die setzt die Datenbank. Leere Texte werden zu null; Namen werden getrimmt.
 */
export function transferToRow(t, playerId) {
  return {
    player_id: playerId ?? t.playerId,
    transfer_date: str(t.date),
    kind: str(t.kind),
    from_club: orNull(t.fromClub),
    from_club_logo_path: t.fromClubLogoPath || null,
    to_club: orNull(t.toClub),
    to_club_logo_path: t.toClubLogoPath || null,
    note: orNull(t.note),
  };
}

/** Die beiden Verweis-Spalten einer Zeile (nur mit clubColumns.js schreiben, siehe oben). */
export function transferClubIdsToRow(t) {
  return { from_club_id: t.fromClubId || null, to_club_id: t.toClubId || null };
}

/** Neueste zuerst: Datum absteigend, bei gleichem Datum der später angelegte Eintrag zuerst. Verändert die Eingabe nicht. */
export function sortTransfers(list) {
  return (list || []).slice().sort((a, b) => {
    const byDate = str(b.date).localeCompare(str(a.date));
    if (byDate !== 0) return byDate;
    return str(b.createdAt).localeCompare(str(a.createdAt));
  });
}

/**
 * Prüfung vor dem Speichern. Liefert { ok, errors }; errors sind Schlüssel unter
 * transferHistory.validation.* (Texte in den Sprachdateien).
 * Pflicht: Datum, Art, mindestens ein Vereinsname; Notiz höchstens NOTE_MAX Zeichen.
 */
export function validateTransfer(t) {
  const errors = [];
  if (!ISO_DATE.test(str(t.date))) errors.push('dateRequired');
  if (!TRANSFER_KINDS.includes(t.kind)) errors.push('kindRequired');
  const noClub = (name, id) => !str(name).trim() && !id;
  if (noClub(t.fromClub, t.fromClubId) && noClub(t.toClub, t.toClubId)) errors.push('clubRequired');
  if (str(t.note).length > NOTE_MAX) errors.push('noteTooLong');
  return { ok: errors.length === 0, errors };
}

/**
 * Hinweis, kein Fehler: Sieht die Notiz nach einem Betrag aus (€, $, EUR, Mio)?
 * Beträge gehören in die geschützten Vertragsdaten, nicht in die Historie, die
 * auch Gäste lesen. Die Datenbank kann das nicht verhindern, die Oberfläche warnt.
 */
export function noteLooksLikeAmount(note) {
  return /[€$]|\bEUR\b|\bMio\b/i.test(str(note));
}

/**
 * Art des Wechsels (oben links in der Zeile) als Übersetzungsschlüssel:
 * permanent, loan, loan_end. Unbekannte Werte fallen auf "permanent" zurück.
 */
export function transferLabel(kind) {
  return { key: `transferHistory.kind.${TRANSFER_KINDS.includes(kind) ? kind : 'permanent'}` };
}

/** Leerer Entwurf für "Eintrag hinzufügen". */
export function emptyTransfer() {
  return {
    date: '', kind: 'permanent',
    fromClub: '', fromClubLogoPath: null, fromClubId: null,
    toClub: '', toClubLogoPath: null, toClubId: null,
    note: '',
  };
}

/**
 * Vorbefüllung "Aus aktuellem Verein übernehmen" — gedacht für Spieler, die schon
 * transferiert sind: aufnehmender Verein = aktueller Verein (aus dem Register, sonst Text) samt Logo,
 * abgebender Verein = der eigene Verein "Grassrunners FC" (aus dem Register, wenn dort vorhanden, sonst nur
 * der Name aus domain/clubs.js), Art Permanent. Das Datum bleibt leer: Es ist Pflicht und wird nicht geraten.
 * Liest den Spieler nur, verändert ihn nicht.
 *
 * @param {object} player
 * @param {Array} [clubs] Vereine des Registers (rowToClub); ohne sie nur Text
 */
export function transferFromCurrentClub(player, clubs = []) {
  const p = player || {};
  const byId = clubsById(clubs);
  const own = (clubs || []).find((c) => clubKey(c.name) === clubKey(CLUBS[0].name));
  const from = own
    ? resolveClub(byId, own.id)
    : { id: null, name: CLUBS[0].name, logoPath: null };
  const to = resolveClub(byId, p.currentClubId, p.currentClub, p.currentClubLogoPath);
  return {
    ...emptyTransfer(),
    fromClub: from.name,
    fromClubLogoPath: from.logoPath,
    fromClubId: from.id,
    toClub: str(to.name),
    toClubLogoPath: to.logoPath,
    toClubId: to.id,
  };
}

/** Alle Logo-Pfade der Einträge, ohne Dubletten. */
export function transferLogoPaths(items) {
  const out = new Set();
  for (const t of items || []) {
    if (t.fromClubLogoPath) out.add(t.fromClubLogoPath);
    if (t.toClubLogoPath) out.add(t.toClubLogoPath);
  }
  return [...out];
}
